import React, { useState, useMemo } from 'react';
import { Spin, Table, Tooltip, Space, Grid, theme, Row, Typography, Progress, Divider, Tag } from 'antd';
import { Localization, _T } from "../../base-react/src/Localization";
import { CalendarOutlined, ClockCircleOutlined, CommentOutlined, EyeInvisibleOutlined, HourglassOutlined, InboxOutlined, InsertRowAboveOutlined, MenuOutlined, NumberOutlined, PartitionOutlined, ReadOutlined, RollbackOutlined, TagOutlined, TeamOutlined, UserOutlined } from '@ant-design/icons';
import { hooks, models } from '@vote4dance/judging-sdk';
var Stream = hooks.streams.Stream;
var JudgingSource = models.judging.JudgingSource;
var ScheduleSource = models.schedules.ScheduleSource;
import { RoundItem } from "../../base-react/models/rounds";
import { flattenTree } from '../pages/event/schedule/utilities';
import { RoundAddTeamsButton } from '../pages/competition/round/components/RoundAddTeamsButton';
import { Link } from 'react-router-dom';
import { RoundStatus } from './RoundStatus';
import { RoundPathParent, findValidRound } from './RoundPathParent';
import { RoundMultiEditSettings } from '../pages/competition/round/components/RoundOverviewSettings';
import { useCompetition } from '../../base-react/hooks/Competition';
const { useBreakpoint } = Grid;
const { useToken } = theme;
const { Text } = Typography;
export const RoundTable = (props) => {
    const { event_id, data, isSortable, components, loading, footer, mode, onSelection, disabled } = props;
    const [inputCompetition] = useCompetition(event_id);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [expandedRowKeys, setExpandedRowKeys] = useState([]);
    const [judging, loadingJudging] = Stream(inputCompetition && JudgingSource(inputCompetition.federation_id));
    const [schedule, loadingSchedule] = Stream(ScheduleSource(event_id));
    const screens = useBreakpoint();
    const [highlightedClassId, setHighlightedClassId] = useState(null);
    const { token } = useToken();
    const getRoundsFromIds = (ids) => {
        const flattenedTree = flattenTree(data);
        const rounds = flattenedTree.filter(round => ids.includes(round.rowKey));
        return rounds;
    };
    const getJudgingTextFromRound = (round) => {
        if (round) {
            const itemJudging = judging && judging.find((a) => a.judging_id == round.judging_id);
            if (itemJudging) {
                return RoundItem.isParentRound(round) ? _T('judging-parent.' + round.judging_type) : itemJudging.judging_label && Localization.getLocalizedString(JSON.parse(itemJudging.judging_label));
            }
            return React.createElement(Text, { type: "danger" }, _T('judging.unsupported'));
        }
        return null;
    };
    const formatRoundTime = (rawTime) => {
        if (!rawTime) {
            return '';
        }
        const parts = rawTime.split(':');
        if (parts.length >= 2) {
            const hours = parts[0].padStart(2, '0');
            const minutes = parts[1].padStart(2, '0');
            return `${hours}:${minutes}`;
        }
        return rawTime;
    };
    const getRoundOverrunSeconds = (round) => {
        var _a;
        if (round.children && round.isPlaceholder) {
            const sum = round.children.reduce((total, child) => {
                const value = Number(child.round_overrun_seconds);
                return Number.isNaN(value) ? total : total + value;
            }, 0);
            return sum !== 0 ? sum : undefined;
        }
        return round.children ? (_a = findValidRound(round.children)) === null || _a === void 0 ? void 0 : _a.round_overrun_seconds : round.round_overrun_seconds;
    };
    const formatDelay = (value) => {
        if (value == null || Number.isNaN(value) || value === 0) {
            return null;
        }
        const sign = value > 0 ? '+' : value < 0 ? '-' : '';
        const abs = Math.abs(value);
        if (abs < 60) {
            return `${sign}${abs}s`;
        }
        const minutes = Math.floor(abs / 60);
        const seconds = abs % 60;
        const paddedMinutes = String(minutes).padStart(2, '0');
        const paddedSeconds = String(seconds).padStart(2, '0');
        return `${sign}${paddedMinutes}:${paddedSeconds}`;
    };
    const memoizedJudging = useMemo(() => {
        return judging;
    }, []);
    const hasEstimatedTime = data.some((item) => {
        var _a;
        const timeValue = item.children ? (_a = findValidRound(item.children)) === null || _a === void 0 ? void 0 : _a.round_estimated_time : item.round_estimated_time;
        return !!timeValue;
    });
    const hasScheduleStartTime = useMemo(() => {
        if (!schedule || schedule.length < 1) {
            return false;
        }
        const scheduleIds = new Set();
        for (const item of data) {
            if (item.schedule_id != null) {
                scheduleIds.add(item.schedule_id);
            }
        }
        if (scheduleIds.size < 1) {
            return false;
        }
        return schedule.some((item) => scheduleIds.has(item.schedule_id) && !!item.schedule_start_time);
    }, [schedule, data]);
    const showTimeColumns = hasEstimatedTime || hasScheduleStartTime;
    const isValidColumn = (column) => {
        if (!column) {
            return false;
        }
        return Object.keys(column).length > 0;
    };
    const columnDefinitions = [
        {
            title: React.createElement(Tooltip, { title: _T('schedule.column.round') },
                React.createElement(InboxOutlined, null)),
            dataIndex: 'round_label',
            key: 'round_label',
            shouldCellUpdate: ((record, prevRecord) => record.round_label != prevRecord.round_label ||
                record.round_new_teams != prevRecord.round_new_teams ||
                record.round_comment != prevRecord.round_comment ||
                record.class_id != highlightedClassId ||
                record.class_id == highlightedClassId),
            render: (text, record) => {
                const uniqueClassIds = data.map(round => round.class_id).filter((x, i, a) => a.indexOf(x) == i);
                const shouldMarkRow = uniqueClassIds.length > 1 ? (record.class_id == highlightedClassId ? true : false) : false;
                return React.createElement(RoundPathParent, { record: record }, (round, parents) => React.createElement(Row, { align: "middle", style: {
                        borderBottom: shouldMarkRow ? '1px solid ' + token.colorPrimary : undefined,
                        marginBottom: shouldMarkRow ? -1 : undefined
                    } },
                    React.createElement(Space, null,
                        React.createElement(Space, { separator: React.createElement(Divider, { orientation: "vertical" }) },
                            mode != "class" &&
                                RoundItem.getClassLabel(round),
                            mode != "class" ? parents ? parents.map((parent, index) => React.createElement(Link, { to: `../comp/${parent.competition_id}/classes/${parent.class_id}/${parent.round_id}`, key: index }, RoundItem.getRoundLabel(parent))) :
                                React.createElement(Link, { to: `../comp/${round.competition_id}/classes/${round.class_id}/${round.round_id}` }, RoundItem.getRoundLabel(round))
                                : (React.createElement(Link, { to: `${round.class_id}/${round.round_id}` }, RoundItem.getRoundLabel(round)))),
                        screens.lg ? React.createElement(Text, { type: "secondary" }, round.round_comment) : screens.md ? React.createElement(Tooltip, { title: round.round_comment },
                            React.createElement(CommentOutlined, { style: { color: round.round_comment ? token.colorPrimary : undefined } })) : ' ',
                        React.createElement(RoundAddTeamsButton, { round: round }))));
            }
        },
        {
            title: React.createElement(Tooltip, { title: _T('schedule.column.beat') },
                React.createElement(NumberOutlined, null)),
            dataIndex: 'round_beat',
            responsive: ['lg'],
            width: 60,
            shouldCellUpdate: ((record, prevRecord) => {
                var _a, _b;
                return record.round_beat != prevRecord.round_beat ||
                    ((_a = findValidRound(record.children)) === null || _a === void 0 ? void 0 : _a.round_beat) != ((_b = findValidRound(prevRecord.children)) === null || _b === void 0 ? void 0 : _b.round_beat);
            }),
            render: ((text, round) => { var _a; return round.children ? React.createElement(Text, { type: 'secondary' }, (_a = findValidRound(round.children)) === null || _a === void 0 ? void 0 : _a.round_beat) : round.round_beat; }),
        },
        {
            title: React.createElement(Tooltip, { title: _T('schedule.column.floor') },
                React.createElement(InsertRowAboveOutlined, null)),
            dataIndex: 'floor_code',
            responsive: ['lg'],
            shouldCellUpdate: ((record, prevRecord) => record.floor_code != prevRecord.floor_code || record.floor_name != prevRecord.floor_name),
            render: ((text, record) => React.createElement(RoundPathParent, { record: record }, (round) => round.floor_code || round.floor_name))
        },
        showTimeColumns ? {
            title: React.createElement(Tooltip, { title: _T('schedule.column.start') },
                React.createElement(ClockCircleOutlined, null)),
            dataIndex: 'round_estimated_time',
            width: 55,
            shouldCellUpdate: ((record, prevRecord) => {
                var _a, _b;
                return formatRoundTime(record.children ? (_a = findValidRound(record.children)) === null || _a === void 0 ? void 0 : _a.round_estimated_time : record.round_estimated_time) !=
                    formatRoundTime(prevRecord.children ? (_b = findValidRound(prevRecord.children)) === null || _b === void 0 ? void 0 : _b.round_estimated_time : prevRecord.round_estimated_time);
            }),
            render: ((text, round) => {
                var _a;
                return (React.createElement(Text, { type: "secondary" }, formatRoundTime(round.children ? (_a = findValidRound(round.children)) === null || _a === void 0 ? void 0 : _a.round_estimated_time : round.round_estimated_time)));
            }),
            responsive: ['xl']
        } : undefined,
        showTimeColumns ? {
            title: React.createElement(Tooltip, { title: _T('schedule.column.offset') },
                React.createElement(HourglassOutlined, null)),
            dataIndex: 'round_overrun_seconds',
            shouldCellUpdate: ((record, prevRecord) => getRoundOverrunSeconds(record) != getRoundOverrunSeconds(prevRecord)),
            render: ((text, record) => {
                const rawOffset = getRoundOverrunSeconds(record);
                const offsetValue = rawOffset != null && rawOffset !== '' ? Number(rawOffset) : undefined;
                const label = formatDelay(offsetValue);
                if (!label) {
                    return null;
                }
                const offsetStyle = offsetValue && offsetValue > 0
                    ? { color: token.colorError }
                    : { color: token.colorSuccess };
                return React.createElement(Text, { style: offsetStyle }, label);
            }),
            responsive: ['xl']
        } : undefined,
        {
            responsive: ['xl'],
            render: ((text, record) => React.createElement(RoundPathParent, { record: record }, (round) => {
                let roundProgress = 0;
                let type = undefined;
                let showInfo = false;
                let strokeColor = undefined;
                const status = round.round_status;
                if (status == "ready") {
                    roundProgress = round.round_judges_done > 0 ? 50 : 25;
                    type = "exception";
                }
                else if (status == "closed") {
                    roundProgress = 75;
                    strokeColor = token.colorWarning;
                }
                else if (status == "confirmed") {
                    roundProgress = round.round_published > 0 ? 100 : 90;
                    type = "success";
                    showInfo = round.round_published > 0;
                }
                else if (round.round_count > 0) {
                    roundProgress = 10;
                }
                return (React.createElement(Progress, { type: "circle", size: 32, percent: roundProgress, status: type, showInfo: showInfo, strokeColor: strokeColor }));
            }))
        },
        {
            title: React.createElement(Tooltip, { title: _T('schedule.column.judging') },
                React.createElement(ReadOutlined, null)),
            dataIndex: 'judging_id',
            responsive: ['md'],
            shouldCellUpdate: ((record, prevRecord) => judging != memoizedJudging ||
                record.judging_id != prevRecord.judging_id ||
                record.round_parent_id != prevRecord.round_parent_id),
            render: ((text, record) => React.createElement(RoundPathParent, { record: record }, (round) => getJudgingTextFromRound(round)))
        },
        {
            title: React.createElement(Tooltip, { title: _T('schedule.column.status') },
                React.createElement(TagOutlined, null)),
            dataIndex: 'round_status',
            responsive: ['sm'],
            render: ((text, record) => React.createElement(RoundPathParent, { record: record }, (round) => React.createElement(Space, null,
                React.createElement(RoundStatus, { round: round }),
                _T(`round.status.${round.round_status}.title`))))
        },
        {
            title: React.createElement(Tooltip, { title: _T('schedule.column.competitors') },
                React.createElement(TeamOutlined, null)),
            dataIndex: 'round_count',
            responsive: ['xl'],
            shouldCellUpdate: ((record, prevRecord) => record.round_count != prevRecord.round_count),
            render: ((text, record) => React.createElement(RoundPathParent, { record: record }, (round) => React.createElement(Text, { type: "secondary" }, round.round_count)))
        },
        {
            title: React.createElement(Tooltip, { title: _T('schedule.column.through') },
                React.createElement(RollbackOutlined, null)),
            dataIndex: 'round_num_through',
            responsive: ['md'],
            shouldCellUpdate: ((record, prevRecord) => record.round_num_through != prevRecord.round_num_through),
            render: ((text, record) => React.createElement(RoundPathParent, { record: record }, (round) => React.createElement(Text, { type: "success" }, round.round_num_through)))
        },
        {
            title: React.createElement(Tooltip, { title: _T('schedule.column.heats') },
                React.createElement(PartitionOutlined, null)),
            dataIndex: 'round_num_heats',
            responsive: ['md'],
            shouldCellUpdate: ((record, prevRecord) => record.round_num_heats != prevRecord.round_num_heats),
            render: ((text, record) => React.createElement(RoundPathParent, { record: record }, (round) => React.createElement(Text, { type: "success" }, round.round_num_heats)))
        },
        // Hide column if all round_hide_results_pos are not number
        data.some(item => typeof item.round_hide_results_pos === 'number') ? {
            title: React.createElement(Tooltip, { title: _T('round.details.round_hide_results_pos.label') },
                React.createElement(EyeInvisibleOutlined, null)),
            dataIndex: 'round_hide_results_pos',
            responsive: ['xl'],
            render: ((text, record) => React.createElement(RoundPathParent, { record: record }, (round) => typeof round.round_hide_results_pos === 'number' &&
                React.createElement(Text, { type: "warning" },
                    React.createElement(Tag, { color: "warning" },
                        React.createElement(Tooltip, { title: _T('round.details.round_hide_results_pos.label', { value: round.round_hide_results_pos }) }, round.round_hide_results_pos)),
                    React.createElement(Tooltip, { title: _T(`round.details.round_hide_results_type.${round.round_hide_results_type}`) },
                        round.round_hide_results_type === 'marks' && React.createElement(NumberOutlined, null),
                        round.round_hide_results_type === 'placements' && React.createElement(TagOutlined, null),
                        round.round_hide_results_type === 'partial' && React.createElement(PartitionOutlined, null),
                        round.round_hide_results_type === 'coach' && React.createElement(UserOutlined, null),
                        round.round_hide_results_type === 'all' && React.createElement(EyeInvisibleOutlined, null)))))
        } : {},
        mode == 'class' ?
            {
                title: React.createElement(Tooltip, { title: _T('schedule.column.start') },
                    React.createElement(CalendarOutlined, null)),
                dataIndex: 'schedule_id',
                shouldCellUpdate: ((record, prevRecord) => record.schedule_id != prevRecord.schedule_id),
                render: (text, round) => { var _a; return schedule && ((_a = schedule.find((a) => a.schedule_id == round.schedule_id)) === null || _a === void 0 ? void 0 : _a.schedule_value); },
                responsive: ['xl']
            } : {},
        {
            dataIndex: "sort",
            key: "sort",
            width: 30,
            render: (text, round) => round.dragLoading ? React.createElement(Spin, null) : React.createElement(MenuOutlined, { style: { cursor: "grab", color: "#999" } })
        },
    ].flat();
    const columns = columnDefinitions.filter(isValidColumn);
    const hasNullFloorId = data.every(item => item.floor_id === null);
    if (hasNullFloorId) {
        const floorColumnIndex = columns.findIndex(col => (col === null || col === void 0 ? void 0 : col.dataIndex) === 'floor_code');
        if (floorColumnIndex >= 0) {
            columns.splice(floorColumnIndex, 1);
        }
    }
    if (!isSortable || disabled) {
        const sortColumnIndex = columns.findIndex(col => (col === null || col === void 0 ? void 0 : col.dataIndex) === 'sort');
        if (sortColumnIndex >= 0) {
            columns.splice(sortColumnIndex, 1);
        }
    }
    const expandable = {
        expandedRowKeys: expandedRowKeys,
        rowExpandable: ((record) => record.children != undefined && record.children.length > 0),
        onExpandedRowsChange: ((expandedKeys) => {
            setExpandedRowKeys(expandedKeys.map(key => key));
        })
    };
    const rowSelection = screens.md ? {
        selectedRowKeys: selectedRowKeys,
        checkStrictly: false,
        onChange: (selectedRowKeys) => {
            setSelectedRowKeys(selectedRowKeys);
            const selection = getRoundsFromIds(selectedRowKeys);
            if (onSelection) {
                onSelection(selection);
            }
            if (mode != 'class') {
                setHighlightedClassId((selection && selection.length > 0) ? selection[0].class_id : null);
            }
        }
    } : undefined;
    const mergedFooter = (footer) => {
        if (footer) {
            return (React.createElement(Space, null,
                footer(getRoundsFromIds(selectedRowKeys)),
                React.createElement(RoundMultiEditSettings, { items: getRoundsFromIds(selectedRowKeys), disabled: disabled })));
        }
        return undefined;
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(Table, { columns: columns, components: components, dataSource: data, expandable: expandable, loading: loading || loadingJudging || loadingSchedule, bordered: false, size: "small", pagination: false, rowKey: "rowKey", rowSelection: !disabled ? rowSelection : undefined, footer: () => mergedFooter(footer) })));
};
